import { useState } from "react";
import {
  MapPin,
  Phone,
  EnvelopeSimple,
  Clock,
  ArrowUpRight,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import { SITE, SERVICES } from "@/lib/site";
import { submitContact } from "@/lib/api";
import Seo from "@/components/Seo";
import { PAGE_SEO } from "@/lib/seo";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  inquiry_type: "general",
  message: "",
};

const ContactPage = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || null,
        inquiry_type: form.inquiry_type,
        message: form.message.trim(),
      });
      setStatus({
        state: "success",
        message: "Thanks — your enquiry is in. A Infini10 consultant will reply within 24 hours.",
      });
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      setStatus({ state: "error", message: typeof detail === "string" ? detail : "Validation failed." });
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo page={PAGE_SEO.contact} />
      <p className="sr-only">{PAGE_SEO.contact.h1Fact}</p>
      {/* HERO */}
      <section className="bg-[#fff6ec]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-12 lg:pt-24 lg:pb-16">
          <h1
            data-testid="contact-hero-title"
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#1f2a44] leading-[1.02] max-w-4xl"
          >
            Tell us who you need.<br />
            <span className="italic font-light">We&apos;ll find them.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-slate-600 leading-relaxed">
            Whether you&apos;re scoping a single hire, a full pod, or an audit-grade background-verification programme — we read every message and reply within one business day.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* INFO */}
          <aside className="lg:col-span-5">
            <div className="rounded-3xl bg-[#1f2a44] text-white p-8 lg:p-10 sticky top-28">
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#fe4c1c]">Reach out</p>
              <h2 className="mt-3 font-display text-3xl font-bold leading-tight">
                Quick replies. No call-centre energy.
              </h2>

              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#fe4c1c] text-[#1f2a44] shrink-0">
                    <MapPin size={20} weight="bold" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/60 font-bold">Office</p>
                    <p className="mt-1 text-sm text-white/85 leading-relaxed">{SITE.address}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#fe4c1c] text-[#1f2a44] shrink-0">
                    <Phone size={20} weight="bold" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/60 font-bold">Phone</p>
                    <a href={`tel:${SITE.phoneRaw}`} data-testid="contact-info-phone" className="mt-1 block text-sm text-white/85 hover:text-[#fe4c1c]">
                      {SITE.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#fe4c1c] text-[#1f2a44] shrink-0">
                    <EnvelopeSimple size={20} weight="bold" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/60 font-bold">Email</p>
                    <a href={`mailto:${SITE.email}`} data-testid="contact-info-email" className="mt-1 block text-sm text-white/85 hover:text-[#fe4c1c] break-all">
                      {SITE.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#fe4c1c] text-[#1f2a44] shrink-0">
                    <Clock size={20} weight="bold" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/60 font-bold">Hours</p>
                    <p className="mt-1 text-sm text-white/85">Mon – Sat · 9:30 AM – 7:00 PM IST</p>
                  </div>
                </li>
              </ul>

              <div className="mt-8 pt-8 border-t border-white/15">
                <a
                  href={`mailto:${SITE.email}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#fe4c1c] hover:text-white"
                >
                  Prefer email? Write us directly <ArrowUpRight size={16} weight="bold" />
                </a>
              </div>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-12"
            >
              <h2 className="font-display text-3xl font-bold tracking-tight text-[#1f2a44]">
                Send us a brief
              </h2>
              <p className="mt-2 text-slate-600">
                Share the role, the deadline, or just a problem. We&apos;ll take it from there.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your name" name="name" required value={form.name} onChange={onChange} testId="contact-input-name" />
                <Field label="Work email" name="email" type="email" required value={form.email} onChange={onChange} testId="contact-input-email" />
                <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} testId="contact-input-phone" />
                <div className="flex flex-col gap-2">
                  <label htmlFor="inquiry_type" className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
                    Looking for
                  </label>
                  <select
                    id="inquiry_type"
                    name="inquiry_type"
                    value={form.inquiry_type}
                    onChange={onChange}
                    data-testid="contact-input-inquiry-type"
                    className="h-12 rounded-xl border border-slate-200 px-4 text-sm text-[#0f172a] bg-white focus:border-[#1f2a44] focus:ring-2 focus:ring-[#1f2a44]/15 outline-none transition-colors"
                  >
                    <option value="general">General enquiry</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.slug}>{s.title}</option>
                    ))}
                    <option value="careers">Careers / candidate</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Subject (optional)" name="subject" value={form.subject} onChange={onChange} testId="contact-input-subject" />
                </div>
                <div className="sm:col-span-2 flex flex-col gap-2">
                  <label htmlFor="message" className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
                    Message <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    minLength={5}
                    rows={6}
                    data-testid="contact-input-message"
                    placeholder="Tell us about the role, role count, timeline or any specific requirements..."
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0f172a] bg-white focus:border-[#1f2a44] focus:ring-2 focus:ring-[#1f2a44]/15 outline-none transition-colors resize-y"
                  />
                </div>
              </div>

              {/* Status */}
              {status.state === "success" && (
                <div
                  data-testid="contact-form-success"
                  className="mt-6 flex items-start gap-3 rounded-xl bg-[#1f2a44]/5 border border-[#1f2a44]/20 p-4 text-sm text-[#1f2a44]"
                >
                  <CheckCircle size={20} weight="fill" className="text-[#1f2a44] shrink-0 mt-0.5" />
                  <p>{status.message}</p>
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid="contact-form-error"
                  className="mt-6 flex items-start gap-3 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700"
                >
                  <WarningCircle size={20} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                  <p>{status.message}</p>
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={status.state === "loading"}
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#1f2a44] text-[#fe4c1c] text-sm font-semibold hover:bg-[#152038] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? "Sending..." : "Send message"}
                  {status.state !== "loading" && <ArrowUpRight size={16} weight="bold" />}
                </button>
                <p className="text-xs text-slate-500">
                  By submitting you agree to our team contacting you about your enquiry.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="rounded-3xl overflow-hidden border border-slate-200" data-testid="contact-map">
            <iframe
              title="Infini10 office location"
              src="https://www.google.com/maps?q=Hulimavu+Begur+Bangalore+560076&output=embed"
              width="100%"
              height="380"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testId }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={name} className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="h-12 rounded-xl border border-slate-200 px-4 text-sm text-[#0f172a] bg-white focus:border-[#1f2a44] focus:ring-2 focus:ring-[#1f2a44]/15 outline-none transition-colors"
    />
  </div>
);

export default ContactPage;
